import {
  groundSingleWitnessCharacterMentions,
  type GroundedSingleWitnessCharacterMention,
  type PreparedSingleWitnessCharacterExtractionCall,
  type SingleWitnessCharacterMentionEvidence,
} from '@variorum/character-index'
import type {
  CharacterEntity,
  CharacterLayerDetectorOutput,
  CharacterOccurrence,
} from '../types/characterLayerTypes'

export const PACKAGE_CHARACTER_EVIDENCE_OUTPUT_CONTRACT = 'package-character-evidence-v1'

export interface AdaptPackageCharacterEvidenceArgs {
  rawExtraction: unknown
  preparedCall: Pick<PreparedSingleWitnessCharacterExtractionCall, 'sourceText' | 'sourceMap'>
  chapterId: string
  createdAt?: string
}

const normalizeText = (value: unknown): string => (
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : ''
)

const stableHash = (value: string): string => {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
)

const readEvidenceArray = (rawExtraction: unknown): unknown[] => {
  if (Array.isArray(rawExtraction)) {
    return rawExtraction
  }
  const payload = asRecord(rawExtraction)
  if (!payload) {
    return []
  }
  const candidates = [
    payload.characters,
    payload.entries,
    payload.mentions,
    payload.items,
    payload.results,
    payload.data,
  ]
  return candidates.find(Array.isArray) as unknown[] || []
}

const mapRawEvidence = (item: unknown): SingleWitnessCharacterMentionEvidence | null => {
  const record = asRecord(item)
  if (!record) {
    return null
  }
  const name = normalizeText(
    record.name ||
    record.character ||
    record.canonical_name ||
    record.canonicalName ||
    record.form
  )
  const window = normalizeText(
    record.window ||
    record.span ||
    record.evidence ||
    record.quote ||
    record.snippet
  )
  if (!name || !window) {
    return null
  }
  const transliteration = normalizeText(
    record.transliteration ||
    record.romanization ||
    record.romanisation
  )
  const role = Array.isArray(record.roles)
    ? record.roles.map(normalizeText).filter(Boolean).join(', ')
    : normalizeText(record.role || record.mode)
  return {
    name,
    ...(transliteration ? { transliteration } : {}),
    window,
    ...(role ? { role } : {}),
  }
}

const characterKey = (mention: Pick<SingleWitnessCharacterMentionEvidence, 'name' | 'transliteration'>): string => (
  normalizeText(mention.name).toLowerCase() || normalizeText(mention.transliteration).toLowerCase()
)

const buildCharacterId = (chapterId: string, mention: Pick<SingleWitnessCharacterMentionEvidence, 'name' | 'transliteration'>): string => (
  `character-package-${stableHash(`${chapterId}:${characterKey(mention)}:${normalizeText(mention.transliteration)}`)}`
)

const buildOccurrenceId = ({
  chapterId,
  characterId,
  mention,
  rowId,
  witnessId,
  localStart,
  localEnd,
}: {
  chapterId: string
  characterId: string
  mention: GroundedSingleWitnessCharacterMention
  rowId: string
  witnessId: string
  localStart?: number
  localEnd?: number
}): string => (
  `character-occ-package-${stableHash([
    chapterId,
    characterId,
    witnessId,
    rowId,
    localStart ?? '',
    localEnd ?? '',
    mention.name,
    mention.window,
  ].join(':'))}`
)

export const extractPackageCharacterEvidence = (
  rawExtraction: unknown,
): SingleWitnessCharacterMentionEvidence[] => (
  readEvidenceArray(rawExtraction)
    .map(mapRawEvidence)
    .filter((item): item is SingleWitnessCharacterMentionEvidence => Boolean(item))
)

export const adaptPackageCharacterEvidenceToDetectorOutput = ({
  rawExtraction,
  preparedCall,
  chapterId,
  createdAt,
}: AdaptPackageCharacterEvidenceArgs): CharacterLayerDetectorOutput => {
  const timestamp = createdAt || new Date().toISOString()
  const evidence = extractPackageCharacterEvidence(rawExtraction)
  const grounded = groundSingleWitnessCharacterMentions(preparedCall, evidence)
  const charactersByKey = new Map<string, CharacterEntity>()
  const occurrences: CharacterOccurrence[] = []

  grounded
    .filter(mention => mention.status === 'grounded')
    .forEach(mention => {
      const key = characterKey(mention)
      if (!key) {
        return
      }
      let character = charactersByKey.get(key)
      if (!character) {
        const characterId = buildCharacterId(chapterId, mention)
        character = {
          character_id: characterId,
          display_name: mention.name,
          aliases: [],
          status: 'auto',
          origin: 'llm',
          metadata: {
            output_contract: PACKAGE_CHARACTER_EVIDENCE_OUTPUT_CONTRACT,
            ...(mention.transliteration ? { transliteration: mention.transliteration } : {}),
            package_evidence: {
              name: mention.name,
              window: mention.window,
            },
          },
          created_at: timestamp,
          updated_at: timestamp,
        }
        charactersByKey.set(key, character)
      }

      mention.sourceAnchors.forEach(anchor => {
        const localStart = typeof anchor.localStart === 'number' ? anchor.localStart : undefined
        const localEnd = typeof anchor.localEnd === 'number' ? anchor.localEnd : undefined
        occurrences.push({
          occurrence_id: buildOccurrenceId({
            chapterId,
            characterId: character!.character_id,
            mention,
            witnessId: anchor.witnessId,
            rowId: anchor.rowId,
            localStart,
            localEnd,
          }),
          character_id: character!.character_id,
          locator: {
            witnessId: anchor.witnessId,
            rowId: anchor.rowId,
            ...(typeof localStart === 'number' ? { localStart } : {}),
            ...(typeof localEnd === 'number' ? { localEnd } : {}),
          },
          role_labels: [mention.role || 'mentioned'],
          label: mention.name,
          summary: mention.window,
          status: 'auto',
          origin: 'llm',
          metadata: {
            output_contract: PACKAGE_CHARACTER_EVIDENCE_OUTPUT_CONTRACT,
            grounding_status: mention.status,
            window: mention.window,
            ...(mention.transliteration ? { transliteration: mention.transliteration } : {}),
            ...(anchor.window ? { anchor_window: anchor.window } : {}),
            ...(anchor.unitUuid ? { unitUuid: anchor.unitUuid } : {}),
            ...(anchor.unitLabel ? { unitLabel: anchor.unitLabel } : {}),
            ...(anchor.unitId ? { unitId: anchor.unitId } : {}),
          },
          created_at: timestamp,
          updated_at: timestamp,
        })
      })
    })

  return {
    characters: Array.from(charactersByKey.values()),
    occurrences,
    notes: [],
    metadata: {
      output_contract: PACKAGE_CHARACTER_EVIDENCE_OUTPUT_CONTRACT,
      evidence_count: evidence.length,
      grounded_count: grounded.filter(mention => mention.status === 'grounded').length,
      ambiguous_count: grounded.filter(mention => mention.status === 'ambiguous').length,
      not_found_count: grounded.filter(mention => mention.status === 'not_found').length,
      grounding: grounded,
    },
  }
}
